/**
 * IFRAP-AI.fr - Scraper Admin Page
 * Interface pour scraper ifrap.org par URL ou mot-clé
 */

import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { Search, Link2, RefreshCw, FileText, ExternalLink, AlertTriangle } from 'lucide-react';

const ScraperPage = () => {
  const [mode, setMode] = useState('url'); // 'url' or 'keyword'
  const [url, setUrl] = useState('');
  const [keyword, setKeyword] = useState('');
  const [maxPages, setMaxPages] = useState(5);
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Charger le statut du scraper
  const loadStatus = async () => {
    try {
      const res = await api.get('/api/content/scraper/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Erreur statut scraper:', err);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  // Lancer un scrape personnalisé
  const launchScrape = async () => {
    setLaunching(true);
    setLaunchResult(null);
    try {
      const payload = {
        max_pages: maxPages,
      };
      if (mode === 'url') {
        payload.url = url;
      } else {
        payload.keyword = keyword;
      }
      const res = await api.post('/api/content/scraper/custom', payload);
      setLaunchResult(res.data);
      // Reload status after a delay
      setTimeout(loadStatus, 5000);
    } catch (err) {
      setLaunchResult({ success: false, message: err.response?.data?.detail || 'Erreur' });
    } finally {
      setLaunching(false);
    }
  };

  // Lancer un scrape complet de toutes les sections
  const launchFullScrape = async () => {
    setLaunching(true);
    try {
      const res = await api.post('/api/content/scraper/start', { max_pages: maxPages });
      setLaunchResult(res.data);
      setTimeout(loadStatus, 10000);
    } catch (err) {
      setLaunchResult({ success: false, message: 'Erreur' });
    } finally {
      setLaunching(false);
    }
  };

  // Rechercher dans le contenu scrapé
  const searchContent = async () => {
    if (!searchQuery) return;
    setSearching(true);
    try {
      const res = await api.get('/api/content/scraper/search', { params: { query: searchQuery, limit: 20 } });
      setSearchResults(res.data);
    } catch (err) {
      console.error('Erreur recherche:', err);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-mono text-[#00FF88]">Scraper IFRAP</h1>
        <p className="text-gray-400 text-sm mt-1">
          Aspirez le contenu d'ifrap.org — études, articles, chiffres clés
        </p>
      </div>

      {/* Statut actuel */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-gray-400">Statut du scraper</h2>
          <button
            onClick={loadStatus}
            className="text-gray-500 hover:text-white text-xs flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Rafraîchir
          </button>
        </div>
        {status ? (
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="text-xs text-gray-500">Articles scrapés</span>
              <p className="text-xl font-bold text-white">{status.content_count || 0}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Dernier scrape</span>
              <p className="text-sm text-white">
                {status.status?.last_run
                  ? new Date(status.status.last_run).toLocaleString('fr-FR')
                  : 'Jamais'}
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Derniers titres</span>
              <div className="text-xs text-gray-400 space-y-0.5">
                {(status.sample_titles || []).map((t, i) => (
                  <p key={i} className="truncate">{t}</p>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Chargement...</p>
        )}
      </div>

      {/* Mode de scraping */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-5">
        <h2 className="text-sm font-medium text-gray-400 mb-4">Nouveau scraping</h2>

        {/* Toggle URL / Mot-clé */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode('url')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
              mode === 'url' ? 'bg-[#00FF88] text-black font-medium' : 'bg-gray-700 text-gray-300'
            }`}
            data-testid="scraper-mode-url"
          >
            <Link2 className="w-4 h-4" /> Par URL
          </button>
          <button
            onClick={() => setMode('keyword')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
              mode === 'keyword' ? 'bg-[#00FF88] text-black font-medium' : 'bg-gray-700 text-gray-300'
            }`}
            data-testid="scraper-mode-keyword"
          >
            <Search className="w-4 h-4" /> Par mot-clé
          </button>
        </div>

        {mode === 'url' ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">URL à scraper</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.ifrap.org/budget-et-comptes-publics/..."
                className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
                data-testid="scraper-url-input"
              />
              <p className="text-[10px] text-gray-600 mt-1">
                Entrez l'URL d'un article, d'une étude ou d'une section ifrap.org
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Mot-clé à rechercher</label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Ex: retraites, dette publique, fonction publique..."
                className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
                data-testid="scraper-keyword-input"
              />
              <p className="text-[10px] text-gray-600 mt-1">
                Le scraper cherchera sur ifrap.org et aspirera les articles correspondants
              </p>
            </div>
          </div>
        )}

        <div className="mt-3">
          <label className="text-xs text-gray-500 mb-1 block">Pages max à scraper</label>
          <input
            type="number"
            value={maxPages}
            onChange={(e) => setMaxPages(parseInt(e.target.value) || 5)}
            min={1}
            max={20}
            className="w-24 px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
          />
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={launchScrape}
            disabled={launching || (mode === 'url' ? !url : !keyword)}
            className="flex-1 py-2.5 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
            data-testid="launch-custom-scrape"
          >
            {launching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {launching ? 'Lancement...' : 'Lancer le scraping'}
          </button>
          <button
            onClick={launchFullScrape}
            disabled={launching}
            className="px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 text-sm"
            data-testid="launch-full-scrape"
          >
            Scraper tout ifrap.org
          </button>
        </div>

        {launchResult && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${
            launchResult.success ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}>
            {launchResult.message}
          </div>
        )}
      </div>

      {/* Recherche dans le contenu scrapé */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-5">
        <h2 className="text-sm font-medium text-gray-400 mb-3">Rechercher dans le contenu scrapé</h2>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchContent()}
            placeholder="Rechercher..."
            className="flex-1 px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
            data-testid="scraper-search-input"
          />
          <button
            onClick={searchContent}
            disabled={searching || !searchQuery}
            className="px-4 py-2 bg-[#000091] text-white rounded text-sm hover:bg-[#000091]/80 disabled:opacity-50"
            data-testid="scraper-search-btn"
          >
            {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Chercher'}
          </button>
        </div>

        {searchResults && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-gray-500">
              {searchResults.results_count} résultat(s) pour "{searchResults.query}"
            </p>
            {searchResults.results?.map((r, i) => (
              <div key={i} className="bg-black/20 rounded-lg p-3 border border-gray-700/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-white">{r.title}</h4>
                    <p className="text-xs text-gray-400 mt-1">{r.summary}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px]">
                      <span className="text-[#00FF88]">{r.category}</span>
                      <span className="text-gray-600">{r.source_type}</span>
                      {r.key_figures?.length > 0 && (
                        <span className="text-[#FFB000]">
                          {r.key_figures.length} chiffre(s) clé(s)
                        </span>
                      )}
                    </div>
                  </div>
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white ml-2">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScraperPage;
